/**
 * Generated Art.14 report package (POST /api/art14/:id/package) + the pure
 * serialization choice behind every Download button — see report-docs.test.ts.
 */

/** The three SRP notification levels of one Art.14 case (Q16 field 2). */
export type SrpLevel = '24h' | '72h' | 'final';

/** Q16 field mandatory at the generated level but still empty (gates "Sent"). */
export interface MissingMandatory {
  field: string;
  label: string;
}

/** One Q16 row as the backend builder returns it (srp-package.ts contract). */
export interface ServerQ16Field {
  field: string;
  label: string;
  milestones: { h24: string; h72: string; final: string };
  source: 'auto' | 'manual';
  value: string | null;
  hint?: string;
}

/** Response of POST /api/art14/:id/package (backend contract). */
export interface PackageResult {
  level?: SrpLevel;
  missingMandatory?: MissingMandatory[];
  q16?: ServerQ16Field[];
  files?: unknown;
  documents?: Record<string, unknown>;
  fieldSheet?: unknown;
}

/** The one non-JSON artifact in the package: the Q16 field sheet (markdown). */
export const FIELD_SHEET_NAME = 'field-sheet.md';

/** Client-side synthesized doc: the Q16 prefill as JSON (pkg.q16). */
export const Q16_NAME = 'q16-fields.json';

/**
 * Report-stage key → SRP notification level (Q16 field 2). The SRP knows three
 * levels; 14d (vuln final), 1m (incident final) and complete all map to 'final'.
 */
export function levelForStage(stageKey: string): SrpLevel {
  if (stageKey === '24h') return '24h';
  if (stageKey === '72h') return '72h';
  return 'final';
}

export interface BlobParts {
  text: string;
  mime: string;
}

/**
 * Pick the bytes + mime for downloading one generated document.
 *  - `field-sheet.md`    → pkg.fieldSheet as text/markdown (only when a string).
 *  - `q16-fields.json`   → pkg.q16 pretty-printed (the SRP form prefill).
 *  - anything else       → pkg.documents[fname]; strings pass through untouched,
 *    JSON docs are pretty-printed. Always application/json.
 * Returns null when the document is not in the package.
 */
export function docToBlobParts(fname: string, pkg: PackageResult | null): BlobParts | null {
  if (!pkg) return null;
  if (fname === FIELD_SHEET_NAME) {
    return typeof pkg.fieldSheet === 'string' ? { text: pkg.fieldSheet, mime: 'text/markdown' } : null;
  }
  if (fname === Q16_NAME) {
    return pkg.q16 == null ? null : { text: JSON.stringify(pkg.q16, null, 2), mime: 'application/json' };
  }
  const doc = pkg.documents?.[fname];
  if (doc == null) return null;
  return { text: typeof doc === 'string' ? doc : JSON.stringify(doc, null, 2), mime: 'application/json' };
}

/**
 * Split the generated package into presentation groups (ENISA-rules review):
 * the SRP takes MANUAL FORM ENTRY — its artifacts are the Q16 prefill + field
 * sheet. CSAF/CycloneDX files serve the manufacturer's Art.14(8) user duty and
 * customers; the NIS2 draft goes to the CSIRT (a separate obligation). Nothing
 * in groups 2–3 is "sent to ENISA".
 */
export interface DocGroups {
  srp: string[];
  users: string[];
  other: string[];
}

export function groupDocs(pkg: PackageResult | null): DocGroups {
  const srp: string[] = [];
  if (pkg?.q16 != null) srp.push(Q16_NAME);
  if (typeof pkg?.fieldSheet === 'string') srp.push(FIELD_SHEET_NAME);
  const users: string[] = [];
  const other: string[] = [];
  for (const name of Object.keys(pkg?.documents ?? {})) {
    if (name === 'nis2-draft.json') other.push(name);
    else users.push(name);
  }
  return { srp, users, other };
}
